// src/components/pages/Movimento/edit/modules/itens/CadastroRapidoProdutoXml/useCadastroRapidoProdutoXml.js
import { useCallback, useEffect, useRef, useState } from 'react';

import { buscarProdutosSemelhantes, cadastrarRapidoProdutoXml, salvarProdutoEmbalagem, prepararProdutoExistenteXml } from '../../../../../../../api/produto';
import { retornaNCMByCodigo } from '../../../../../../../api/NCM';
import * as DataListAPI from '../../../../../../../api/dataList';
import * as SubGrupoAPI from '../../../../../../../api/subGrupo';
import { resolveEmpresaTabela } from '../../../../../../../utils/funcoes';
import * as TabelaAPI from '../../../../../../../api/tabela';

const normalizarGtin = (valor) => {
    const codigo = String(valor || '')
        .trim()
        .toUpperCase();

    if (!codigo || codigo === 'SEM GTIN' || codigo === 'SEM GTIN TRIB' || /^0+$/.test(codigo)) {
        return '';
    }

    return codigo;
};

const normalizarBooleano = (valor, padrao = false) => {
    if (valor === undefined || valor === null || valor === '') {
        return padrao;
    }

    if (typeof valor === 'string') {
        const valorNormalizado = valor.trim().toLowerCase();

        if (['true', '1', 'sim'].includes(valorNormalizado)) {
            return true;
        }

        if (['false', '0', 'nao', 'não'].includes(valorNormalizado)) {
            return false;
        }
    }

    return Boolean(valor);
};

// O DataList confirma um vínculo apenas quando o texto identifica uma opção única.
// Não consulta o DOM: outros datalists podem ter opções com a mesma descrição.
const encontrarOpcaoUnica = (opcoes, valor) => {
    const texto = String(valor ?? '')
        .trim()
        .toLocaleUpperCase('pt-BR');
    if (!texto) return null;

    const encontradas = (Array.isArray(opcoes) ? opcoes : []).filter(
        (opcao) =>
            opcao?.id != null &&
            String(opcao.descricao ?? '')
                .trim()
                .toLocaleUpperCase('pt-BR') === texto
    );

    return encontradas.length === 1 ? encontradas[0] : null;
};

const criarEstadoInicial = (itemXml, empresaId, parametro) => ({
    empresaId: itemXml?.empresaId ?? empresaId ?? '',

    descricao: itemXml?.descricao ?? itemXml?.xProd ?? '',

    ncmCodigo: itemXml?.NCM ?? '',
    ncmId: '',
    ncm: null,
    ncmNaoLocalizado: false,
    carregandoNcm: false,

    grupoId: '',
    subGrupoId: '',

    venda: normalizarBooleano(parametro?.produtoCadastroRapidoVendaPadrao, true),

    materiaPrima: normalizarBooleano(parametro?.produtoCadastroRapidoMateriaPrimaPadrao, false),

    embalagem: false,
    usoConsumo: false,

    gtinComercial: normalizarGtin(itemXml?.gtinComercial ?? itemXml?.cEAN),

    gtinTributavel: normalizarGtin(itemXml?.gtinTributavel ?? itemXml?.cEANTrib),

    embalagemComercialId: '',
    embalagemTributavelId: '',
    embalagemPadraoId: '',
    produtoEmbalagemExistenteId: '',
});

export default function useCadastroRapidoProdutoXml({
    open,
    itemXml,
    empresaId,
    parametro,
    onProdutoCriado,
    onCodigoVinculado,
    setFlashMessage,
}) {
    const [dados, setDados] = useState(() => criarEstadoInicial(itemXml, empresaId, parametro));

    const [produtoSelecionado, setProdutoSelecionado] = useState(null);
    const [Grupo, setGrupo] = useState({});
    const [SubGrupo, setSubGrupo] = useState({});
    const [optionsGrupo, setOptionsGrupo] = useState([]);
    const [optionsSubGrupo, setOptionsSubGrupo] = useState([]);
    const [grupoLoading, setGrupoLoading] = useState(false);
    const [subGrupoLoading, setSubGrupoLoading] = useState(false);

    // Respostas de uma pesquisa anterior não podem sobrescrever a seleção atual.
    const grupoRequestId = useRef(0);
    const subGrupoRequestId = useRef(0);
    const grupoTimerRef = useRef(null);
    const [optionsEmbalagem, setEmbalagem] = useState([]);

    const [produtosSemelhantes, setProdutosSemelhantes] = useState([]);

    const [loadingSemelhantes, setLoadingSemelhantes] = useState(false);
    const [loading, setLoading] = useState(false);
    const [preparandoProduto, setPreparandoProduto] = useState(false);
    const [conflitoCadastro, setConflitoCadastro] = useState([]);
    const selecaoProdutoRequestId = useRef(0);
    const operacaoEmAndamentoRef = useRef(false);
    const embalagensProdutoExistente = (produtoSelecionado?.produtoEmbalagens || []).map((vinculo) => ({
        id: vinculo.id,
        descricao: `${vinculo.Embalagem?.descricao || 'Embalagem'} — fator ${vinculo.Embalagem?.fator ?? '?'} — código ${vinculo.codigoBarras || 'não informado'}`,
    }));

    const empresaTabelaId = resolveEmpresaTabela(parametro, dados?.empresaId ?? empresaId);
    const vendaPadrao = normalizarBooleano(parametro?.produtoCadastroRapidoVendaPadrao, true);
    const materiaPrimaPadrao = normalizarBooleano(parametro?.produtoCadastroRapidoMateriaPrimaPadrao, false);

    const carregarSubGrupos = useCallback(
        async (grupoId) => {
            const requestId = ++subGrupoRequestId.current;
            setOptionsSubGrupo([]);

            if (!grupoId) {
                setSubGrupoLoading(false);
                return [];
            }

            setSubGrupoLoading(true);

            try {
                // Mesmo endpoint e formato usados no cadastro normal de Produto.
                const response = await SubGrupoAPI.listar(grupoId);
                const lista = Array.isArray(response?.data?.data)
                    ? response.data.data.filter((subGrupo) => subGrupo?.ativo === true || Number(subGrupo?.ativo) === 1)
                    : [];

                if (requestId !== subGrupoRequestId.current) return [];

                setOptionsSubGrupo(lista);
                return lista;
            } catch (error) {
                if (requestId === subGrupoRequestId.current) {
                    setOptionsSubGrupo([]);
                    setFlashMessage?.(
                        error?.response?.data?.message || 'Não foi possível carregar os subgrupos.',
                        error?.response?.data?.type || 'error'
                    );
                }
                return [];
            } finally {
                if (requestId === subGrupoRequestId.current) {
                    setSubGrupoLoading(false);
                }
            }
        },
        [setFlashMessage]
    );

    useEffect(() => {
        selecaoProdutoRequestId.current += 1;
        setPreparandoProduto(false);
        setConflitoCadastro([]);
        // Invalida pesquisas ao fechar o diálogo ou trocar o item/empresa.
        clearTimeout(grupoTimerRef.current);
        grupoRequestId.current += 1;
        subGrupoRequestId.current += 1;
        setGrupoLoading(false);
        setSubGrupoLoading(false);
        setGrupo({});
        setSubGrupo({});
        setOptionsGrupo([]);
        setOptionsSubGrupo([]);

        if (!open) return;

        let ativo = true;
        const estado = criarEstadoInicial(itemXml, empresaId, {
            produtoCadastroRapidoVendaPadrao: vendaPadrao,
            produtoCadastroRapidoMateriaPrimaPadrao: materiaPrimaPadrao,
        });

        setDados(estado);
        setProdutoSelecionado(null);
        setEmbalagem([]);

        const carregarNCM = async () => {
            const codigoNcm = String(itemXml?.NCM || '').trim();

            if (!codigoNcm) {
                setDados((prev) => ({
                    ...prev,
                    ncmId: '',
                    ncm: null,
                    ncmNaoLocalizado: true,
                    carregandoNcm: false,
                }));
                return;
            }

            setDados((prev) => ({
                ...prev,
                ncmCodigo: codigoNcm,
                ncmId: '',
                ncm: null,
                ncmNaoLocalizado: false,
                carregandoNcm: true,
            }));

            try {
                const response = await retornaNCMByCodigo(codigoNcm);
                const ncm = response?.ncm ?? response;

                if (!ativo) return;
                if (!ncm?.id) throw new Error('NCM não localizado');

                setDados((prev) => ({
                    ...prev,
                    ncmId: ncm.id,
                    ncm,
                    ncmNaoLocalizado: false,
                    carregandoNcm: false,
                }));
            } catch (error) {
                if (!ativo) return;
                console.error('Erro ao localizar NCM:', error);

                setDados((prev) => ({
                    ...prev,
                    ncmId: '',
                    ncm: null,
                    ncmNaoLocalizado: true,
                    carregandoNcm: false,
                }));
            }
        };

        const carregarEmbalagens = async () => {
            if (!estado.empresaId) return;

            try {
                const result = await TabelaAPI.tabela(estado.empresaId, 'Embalagem', 'descricao');
                if (!ativo) return;

                setEmbalagem(Array.isArray(result) ? result.filter((embalagem) => embalagem?.entrada === true) : []);
            } catch (error) {
                if (!ativo) return;
                console.error('Erro ao carregar embalagens:', error);
                setEmbalagem([]);
            }
        };

        carregarNCM();
        carregarEmbalagens();

        return () => {
            ativo = false;
            clearTimeout(grupoTimerRef.current);
            grupoRequestId.current += 1;
            subGrupoRequestId.current += 1;
        };
    }, [open, itemXml, empresaId, vendaPadrao, materiaPrimaPadrao]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const descricao = String(dados?.descricao || '').trim();

        if (!dados?.empresaId || descricao.length < 3) {
            setProdutosSemelhantes([]);
            setLoadingSemelhantes(false);
            return;
        }

        let ativo = true;

        const timer = setTimeout(async () => {
            setLoadingSemelhantes(true);

            try {
                const response = await buscarProdutosSemelhantes({
                    empresaId: dados.empresaId,
                    descricao,
                    limite: 10,
                });

                if (!ativo) {
                    return;
                }

                const lista = Array.isArray(response?.produtos) ? response.produtos : [];

                setProdutosSemelhantes(lista);
            } catch (error) {
                if (!ativo) {
                    return;
                }

                console.error('Erro ao buscar produtos semelhantes:', error);

                setProdutosSemelhantes([]);
            } finally {
                if (ativo) {
                    setLoadingSemelhantes(false);
                }
            }
        }, 500);

        return () => {
            ativo = false;
            clearTimeout(timer);
        };
    }, [open, dados?.empresaId, dados?.descricao]);

    const change = useCallback(
        (event) => {
            const { name, value, checked, type } = event?.target || {};
            if (!name) return;
            if (name === 'descricao') {
                setConflitoCadastro([]);
            }

            if (name === 'Grupo.descricao') {
                const texto = String(value ?? '');
                const selecionado = encontrarOpcaoUnica(optionsGrupo, texto);
                const requestId = ++grupoRequestId.current;

                clearTimeout(grupoTimerRef.current);
                subGrupoRequestId.current += 1;
                setGrupoLoading(false);
                setSubGrupoLoading(false);
                setSubGrupo({});
                setOptionsSubGrupo([]);

                setGrupo(selecionado || { descricao: texto });
                setDados((prev) => ({
                    ...prev,
                    grupoId: selecionado?.id ?? '',
                    subGrupoId: '',
                }));

                if (selecionado) {
                    carregarSubGrupos(selecionado.id);
                    return;
                }

                setOptionsGrupo([]);
                if (!texto.trim() || !empresaTabelaId) return;

                setGrupoLoading(true);
                // Aguarda uma pausa na digitação; não carrega todos os grupos.
                grupoTimerRef.current = setTimeout(async () => {
                    try {
                        const response = await DataListAPI.dataList({
                            empresaId: empresaTabelaId,
                            tabela: 'Grupo',
                            condicao: 'produto',
                            filtro: texto.trim(),
                        });

                        if (requestId !== grupoRequestId.current) return;

                        const lista = Array.isArray(response)
                            ? response.filter((grupo) => grupo?.id != null && normalizarBooleano(grupo?.ativo, true))
                            : [];

                        setOptionsGrupo(lista);
                        const grupoEncontrado = encontrarOpcaoUnica(lista, texto);

                        if (grupoEncontrado) {
                            setGrupo(grupoEncontrado);
                            setDados((prev) => ({ ...prev, grupoId: grupoEncontrado.id, subGrupoId: '' }));
                            carregarSubGrupos(grupoEncontrado.id);
                        }
                    } catch (error) {
                        if (requestId !== grupoRequestId.current) return;
                        setOptionsGrupo([]);
                        setFlashMessage?.(
                            error?.response?.data?.message || 'Não foi possível pesquisar os grupos.',
                            error?.response?.data?.type || 'error'
                        );
                    } finally {
                        if (requestId === grupoRequestId.current) {
                            setGrupoLoading(false);
                        }
                    }
                }, 250);
                return;
            }

            if (name === 'SubGrupo.descricao') {
                const texto = String(value ?? '');
                const selecionado = dados.grupoId && !subGrupoLoading ? encontrarOpcaoUnica(optionsSubGrupo, texto) : null;

                setSubGrupo(selecionado || { descricao: texto });
                setDados((prev) => ({ ...prev, subGrupoId: selecionado?.id ?? '' }));
                return;
            }

            // Mantém o tratamento existente dos quatro checkboxes e demais campos.
            setDados((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        },
        [optionsGrupo, optionsSubGrupo, dados.grupoId, empresaTabelaId, subGrupoLoading, carregarSubGrupos, setFlashMessage]
    );

    const selecionarProduto = useCallback(async (produto) => {
        if (operacaoEmAndamentoRef.current) return;
        const requestId = ++selecaoProdutoRequestId.current;
        setProdutoSelecionado(null);
        setDados((prev) => ({ ...prev, produtoEmbalagemExistenteId: '' }));
        setPreparandoProduto(true);
        try {
            const resultado = await prepararProdutoExistenteXml({
                empresaId: Number(dados.empresaId), produtoId: Number(produto?.id),
            });
            if (requestId !== selecaoProdutoRequestId.current) return;
            if (resultado?.type !== 'success' || !resultado?.produto?.id) {
                throw new Error(resultado?.message || 'Produto existente não retornado.');
            }
            const completo = resultado.produto;
            const vinculos = completo.produtoEmbalagens || [];
            setProdutoSelecionado(completo);
            // Só pré-seleciona quando há uma única possibilidade.
            setDados((prev) => ({ ...prev,
                produtoEmbalagemExistenteId: vinculos.length === 1 ? vinculos[0].id : '',
            }));
        } catch (error) {
            if (requestId !== selecaoProdutoRequestId.current) return;
            setFlashMessage?.(error?.response?.data?.message || error.message || 'Erro ao consultar produto existente.', 'warning');
        } finally {
            if (requestId === selecaoProdutoRequestId.current) setPreparandoProduto(false);
        }
    }, [dados.empresaId, setFlashMessage]);

    const limparProdutoSelecionado = useCallback(() => {
        selecaoProdutoRequestId.current += 1;
        setPreparandoProduto(false);
        setProdutoSelecionado(null);
        setDados((prev) => ({ ...prev, produtoEmbalagemExistenteId: '' }));
    }, []);

    const usarProdutoExistente = useCallback(async () => {
        if (operacaoEmAndamentoRef.current || preparandoProduto) return;
        if (!produtoSelecionado?.id || !dados.produtoEmbalagemExistenteId) {
            setFlashMessage?.('Selecione o produto e uma de suas embalagens.', 'warning');
            return;
        }
        if (normalizarGtin(dados.gtinComercial) || normalizarGtin(dados.gtinTributavel)) {
            setFlashMessage?.('Este XML possui GTIN. Utilize a vinculação de códigos.', 'warning');
            return;
        }
        if (typeof onCodigoVinculado !== 'function') {
            setFlashMessage?.('Rotina de aplicação do produto indisponível.', 'error');
            return;
        }
        operacaoEmAndamentoRef.current = true;
        setLoading(true);
        try {
            const resultado = await prepararProdutoExistenteXml({
                empresaId: Number(dados.empresaId), produtoId: Number(produtoSelecionado.id),
                produtoEmbalagemId: Number(dados.produtoEmbalagemExistenteId),
            });
            if (resultado?.type !== 'success' || !resultado?.produto?.id || !resultado?.produtoEmbalagemId) {
                throw new Error(resultado?.message || 'Seleção de embalagem não validada.');
            }
            const aplicado = await onCodigoVinculado(resultado.produto, resultado);
            if (aplicado === false) return;
            setFlashMessage?.('Produto existente aplicado ao item. Nenhum novo cadastro foi criado.', 'success');
        } catch (error) {
            setFlashMessage?.(error?.response?.data?.message || error.message || 'Erro ao utilizar produto existente.', 'warning');
        } finally {
            operacaoEmAndamentoRef.current = false;
            setLoading(false);
        }
    }, [dados, produtoSelecionado, preparandoProduto, onCodigoVinculado, setFlashMessage]);

    const cadastrarNovo = useCallback(async () => {
        const descricao = String(dados?.descricao || '').trim();

        if (!dados?.empresaId) {
            setFlashMessage?.('Empresa não informada.', 'warning');
            return;
        }

        if (!descricao) {
            setFlashMessage?.('Informe a descrição do produto.', 'warning');
            return;
        }

        if (!dados?.ncmId) {
            setFlashMessage?.('NCM não localizado. Entre em contato com o suporte.', 'warning');
            return;
        }

        // Grupo e Subgrupo continuam opcionais, mas texto digitado sem vínculo
        // não pode ser enviado como se fosse uma seleção válida.
        if (grupoLoading || subGrupoLoading) {
            setFlashMessage?.('Aguarde o carregamento do Grupo e Subgrupo.', 'warning');
            return;
        }

        if (String(Grupo?.descricao ?? '').trim() && !dados.grupoId) {
            setFlashMessage?.('Selecione um Grupo válido na lista ou limpe o campo.', 'warning');
            return;
        }

        if (String(SubGrupo?.descricao ?? '').trim() && !dados.subGrupoId) {
            setFlashMessage?.('Selecione um Subgrupo válido na lista ou limpe o campo.', 'warning');
            return;
        }

        if (
            dados.subGrupoId &&
            (!dados.grupoId || !optionsSubGrupo.some((subGrupo) => Number(subGrupo.id) === Number(dados.subGrupoId)))
        ) {
            setFlashMessage?.('O Subgrupo selecionado não pertence ao Grupo atual. Selecione-o novamente.', 'warning');
            return;
        }

        const possuiGtinComercial = Boolean(normalizarGtin(dados?.gtinComercial));

        const possuiGtinTributavel = Boolean(normalizarGtin(dados?.gtinTributavel));

        const semGtinNoXml = !possuiGtinComercial && !possuiGtinTributavel;

        if (possuiGtinComercial && !dados?.embalagemComercialId) {
            setFlashMessage?.('Informe a embalagem do código comercial.', 'warning');
            return;
        }

        if (possuiGtinTributavel && !dados?.embalagemTributavelId) {
            setFlashMessage?.('Informe a embalagem do código tributável.', 'warning');
            return;
        }
        if (semGtinNoXml && !dados?.embalagemPadraoId) {
            setFlashMessage?.('Informe a embalagem padrão do produto.', 'warning');
            return;
        }
        if (operacaoEmAndamentoRef.current) return;
        operacaoEmAndamentoRef.current = true;
        setLoading(true);

        try {
            const resultado = await cadastrarRapidoProdutoXml({
                empresaId: Number(dados.empresaId),

                descricao,

                ncmId: Number(dados.ncmId),

                grupoId: dados.grupoId ? Number(dados.grupoId) : null,
                subGrupoId: dados.subGrupoId ? Number(dados.subGrupoId) : null,

                venda: dados?.venda === true,

                materiaPrima: dados?.materiaPrima === true,

                embalagem: dados?.embalagem === true,

                usoConsumo: dados?.usoConsumo === true,

                gtinComercial: String(dados.gtinComercial || '').trim(),

                gtinTributavel: String(dados.gtinTributavel || '').trim(),

                embalagemComercialId: dados.embalagemComercialId ? Number(dados.embalagemComercialId) : null,

                embalagemTributavelId: dados.embalagemTributavelId ? Number(dados.embalagemTributavelId) : null,

                embalagemPadraoId: dados.embalagemPadraoId ? Number(dados.embalagemPadraoId) : null,
            });

            /*
             * O backend localizou um código ou produto existente.
             * Não tratamos como cadastro concluído.
             */
            if (resultado?.code === 'CODIGO_BARRAS_EXISTENTE' || resultado?.code === 'PRODUTO_EXISTENTE') {
                setProdutoSelecionado(null);
                setDados((prev) => ({ ...prev, produtoEmbalagemExistenteId: '' }));
                setConflitoCadastro(Array.isArray(resultado?.produtos)
                    ? resultado.produtos : resultado?.produto ? [resultado.produto] : []);

                setFlashMessage?.(resultado?.message || 'Produto ou código já cadastrado.', resultado?.type || 'info');

                return;
            }

            const produtoCriado = resultado?.produto;

            if (!produtoCriado?.id) {
                throw new Error('O backend não retornou o produto cadastrado.');
            }

            setFlashMessage?.(resultado?.message || 'Produto cadastrado com sucesso!', 'success');

            onProdutoCriado?.(produtoCriado, resultado);
        } catch (error) {
            console.error('Erro ao cadastrar produto pelo XML:', error);

            setFlashMessage?.(
                error?.response?.data?.message || error?.message || 'Erro ao cadastrar o produto.',
                error?.response?.data?.type || 'error'
            );
        } finally {
            operacaoEmAndamentoRef.current = false;
            setLoading(false);
        }
    }, [dados, Grupo, SubGrupo, optionsSubGrupo, grupoLoading, subGrupoLoading, onProdutoCriado, setFlashMessage]);

    const vincularCodigo = useCallback(async () => {
        if (!produtoSelecionado?.id) {
            setFlashMessage?.('Selecione um produto existente.', 'warning');
            return;
        }

        let possuiEmbalagemPrincipal =
            Array.isArray(produtoSelecionado?.produtoEmbalagens) &&
            produtoSelecionado.produtoEmbalagens.some((item) => item?.principal === true);

        const gtinComercial = normalizarGtin(dados?.gtinComercial);

        const gtinTributavel = normalizarGtin(dados?.gtinTributavel);

        const embalagemComercialId = Number(dados?.embalagemComercialId) || null;

        const embalagemTributavelId = Number(dados?.embalagemTributavelId) || null;

        if (!gtinComercial && !gtinTributavel) {
            return usarProdutoExistente();
        }

        if (gtinComercial && !embalagemComercialId) {
            setFlashMessage?.('Informe a embalagem do código comercial.', 'warning');
            return;
        }

        if (gtinTributavel && !embalagemTributavelId) {
            setFlashMessage?.('Informe a embalagem do código tributável.', 'warning');
            return;
        }

        /*
         * O mesmo código não pode identificar duas embalagens
         * diferentes dentro da mesma empresa.
         */
        if (
            gtinComercial &&
            gtinTributavel &&
            gtinComercial === gtinTributavel &&
            embalagemComercialId !== embalagemTributavelId
        ) {
            setFlashMessage?.('O mesmo GTIN foi informado para embalagens diferentes. Revise os dados do XML.', 'warning');
            return;
        }

        const embalagens = [];

        if (gtinComercial) {
            embalagens.push({
                origem: 'COMERCIAL',
                embalagemId: embalagemComercialId,
                codigoBarras: gtinComercial,
                principal: !gtinTributavel,
            });
        }

        if (gtinTributavel) {
            embalagens.push({
                origem: 'TRIBUTAVEL',
                embalagemId: embalagemTributavelId,
                codigoBarras: gtinTributavel,
                principal: true,
            });
        }

        /*
         * Comercial e tributável podem apontar para exatamente
         * o mesmo código e embalagem. Nesse caso, cria somente
         * um registro e preserva principal=true.
         */
        const embalagensUnicas = Array.from(
            embalagens
                .reduce((mapa, item) => {
                    const chave = `${item.embalagemId}:${item.codigoBarras}`;

                    const existente = mapa.get(chave);

                    if (existente) {
                        existente.principal = existente.principal || item.principal;

                        existente.origens.push(item.origem);
                    } else {
                        mapa.set(chave, {
                            ...item,
                            origens: [item.origem],
                        });
                    }

                    return mapa;
                }, new Map())
                .values()
        );

        if (operacaoEmAndamentoRef.current) return;
        operacaoEmAndamentoRef.current = true;
        setLoading(true);

        try {
            const resultados = [];

            /*
             * Sequencial para evitar concorrência na verificação
             * de código existente.
             */
            for (const embalagem of embalagensUnicas) {
                const definirComoPrincipal = embalagem.principal === true && !possuiEmbalagemPrincipal;
                const resultado = await salvarProdutoEmbalagem({
                    funcao: 'Adicionar',

                    empresaId: Number(dados.empresaId),

                    produtoId: Number(produtoSelecionado.id),

                    embalagemId: embalagem.embalagemId,

                    codigoBarras: embalagem.codigoBarras,

                    ativo: true,
                    principal: definirComoPrincipal,
                    aceitaFracao: false,
                    obterEmpresaPadrao: true,
                });
                if (resultado instanceof Error || resultado?.response?.status >= 400 ||
                    resultado?.type === 'error' || resultado?.type === 'warning') {
                    throw new Error(resultado?.response?.data?.message || resultado?.message || 'Não foi possível vincular o código.');
                }
                if (definirComoPrincipal) {
                    possuiEmbalagemPrincipal = true;
                }
                resultados.push({
                    ...embalagem,
                    resultado,
                });
            }

            /*
             * A última resposta do backend contém a relação atualizada
             * das embalagens pertencentes ao produto.
             */
            const ultimaResposta = resultados[resultados.length - 1]?.resultado;

            const embalagensAtualizadas = Array.isArray(ultimaResposta?.resultado)
                ? ultimaResposta.resultado
                : produtoSelecionado?.produtoEmbalagens || [];

            const produtoAtualizado = {
                ...produtoSelecionado,

                produtoEmbalagens: embalagensAtualizadas,
            };

            setFlashMessage?.(`${resultados.length} código(s) vinculado(s) com sucesso!`, 'success');

            onCodigoVinculado?.(produtoAtualizado, {
                resultados,
                quantidade: resultados.length,
                produtoEmbalagens: embalagensAtualizadas,
            });
        } catch (error) {
            console.error('Erro ao vincular códigos ao produto:', error);

            setFlashMessage?.(
                error?.response?.data?.message || 'Erro ao vincular os códigos.',
                error?.response?.data?.type || 'error'
            );
        } finally {
            operacaoEmAndamentoRef.current = false;
            setLoading(false);
        }
    }, [dados, produtoSelecionado, onCodigoVinculado, setFlashMessage, usarProdutoExistente]);

    return {
        dados,
        produtoSelecionado,

        loading: loading || preparandoProduto,
        loadingSemelhantes,
        embalagensProdutoExistente,
        conflitoCadastro,

        produtosSemelhantes: conflitoCadastro.length ? conflitoCadastro : produtosSemelhantes,

        Grupo,
        SubGrupo,
        optionsGrupo,
        optionsSubGrupo,
        grupoLoading,
        subGrupoLoading,
        optionsEmbalagem,
        actions: {
            change,
            selecionarProduto,
            limparProdutoSelecionado,
            cadastrarNovo,
            vincularCodigo,
        },
    };
}
